import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  Autocomplete,
  Box,
  Card,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material'
import useBaseService from '../../../../hooks/services/useBaseService'
import { entities } from '../../../../constants/entities'

/**
 * Seleção ÚNICA de base, com busca por digitação.
 * mode='devolution': pra qual base o caminhão voltou (devolução).
 * mode='reserve': em qual base o equipamento ficará reservado.
 * Vazio = mantém o endereço atual/padrão do equipamento.
 */
const BaseDestinationSelector = ({
  value,
  onChange,
  mode = 'devolution',
  recipientCustomerId,
}) => {
  const navigate = useNavigate()
  const { get: baseServiceGet } = useBaseService(navigate)
  const [reserveDestinationType, setReserveDestinationType] = useState('base')

  const texts =
    mode === 'replacement'
      ? {
          title: 'Base de destino do equipamento retirado',
          helper:
            'A base selecionada será usada para o equipamento antigo retirado do cliente. O equipamento substituto não será afetado. Se deixar em branco, será mantido o comportamento padrão do sistema.',
        }
      : mode === 'reserve'
        ? {
            title:
              reserveDestinationType === 'customer'
                ? 'Endereço do cliente onde o equipamento ficará reservado'
                : 'Base onde o equipamento ficará reservado',
          helper:
            'Escolha uma base ou um endereço do cliente. Vale para todos os equipamentos reservados neste carregamento. Em branco, mantém o endereço atual do equipamento.',
        }
      : {
          title: 'Base de destino da devolução',
          helper:
            'Digite pra buscar a base. Vale para todos os equipamentos retirados neste carregamento. Em branco, mantém a base padrão do sistema.',
        }

  const {
    data: destinations,
    isLoading,
    isError,
  } = useQuery({
    queryKey:
      mode === 'reserve' && reserveDestinationType === 'customer'
        ? ['customerAddresses', recipientCustomerId]
        : ['organizationAddresses'],
    queryFn: async () => {
      const PAGE_SIZE = 1000

      const fetchPage = async (page) => {
        const addressPath =
          mode === 'reserve' && reserveDestinationType === 'customer'
            ? entities.customers.addresses.get(recipientCustomerId)
            : 'addresses'
        const response = await baseServiceGet(addressPath, {
          page,
          per_page: PAGE_SIZE,
        })
        const result = response?.result ?? {}
        const raw = result.data ?? response?.data ?? []
        return {
          items: Array.isArray(raw) ? raw : [],
          lastPage: result.last_page ?? 1,
        }
      }

      const first = await fetchPage(1)
      let all = first.items

      if (first.lastPage > 1) {
        const rest = await Promise.all(
          Array.from({ length: first.lastPage - 1 }, (_, i) =>
            fetchPage(i + 2).then((p) => p.items)
          )
        )
        all = all.concat(...rest)
      }

      // Só bases quando o destino é a organização; o endpoint do cliente já
      // retorna exclusivamente os endereços daquele cliente.
      const seen = new Set()
      return all
        .filter(
          (a) =>
            (mode === 'reserve' && reserveDestinationType === 'customer') ||
            a.addressable_type === 'organization'
        )
        .filter((a) => (seen.has(a.id) ? false : (seen.add(a.id), true)))
        .sort((a, b) => (a.alias ?? '').localeCompare(b.alias ?? ''))
    },
    staleTime: Infinity,
    enabled:
      !(mode === 'reserve' && reserveDestinationType === 'customer') ||
      !!recipientCustomerId,
  })

  // Alias + cidade/UF pra desambiguar nomes repetidos
  const baseLabel = (base) => {
    const main =
      base.alias ||
      `${base.street ?? ''}${base.number ? `, ${base.number}` : ''}`.trim() ||
      `Endereço ${base.id}`
    const geo = [base.city, base.state].filter(Boolean).join('/')
    return geo ? `${main} — ${geo}` : main
  }

  const selectedDestination = useMemo(
    () => (destinations ?? []).find((address) => address.id === value) ?? null,
    [destinations, value]
  )

  return (
    <Card sx={{ p: 2, mt: 2 }}>
      <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
        {texts.title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {texts.helper}
      </Typography>

      {mode === 'reserve' && (
        <FormControl sx={{ mb: 1 }}>
          <RadioGroup
            row
            value={reserveDestinationType}
            onChange={(event) => {
              setReserveDestinationType(event.target.value)
              onChange(null)
            }}>
            <FormControlLabel value="base" control={<Radio />} label="Base" />
            <FormControlLabel
              value="customer"
              control={<Radio />}
              label="Cliente"
            />
          </RadioGroup>
        </FormControl>
      )}

      {reserveDestinationType === 'customer' && !recipientCustomerId ? (
        <Typography variant="body2" color="text.secondary">
          Selecione o cliente do carregamento para escolher um endereço.
        </Typography>
      ) : isLoading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={18} />
          <Typography variant="body2">
            Carregando {reserveDestinationType === 'customer' ? 'endereços' : 'bases'}...
          </Typography>
        </Box>
      ) : isError ? (
        <Typography variant="body2" color="error">
          Não foi possível carregar os endereços para seleção de destino.
          Contate o desenvolvimento.
        </Typography>
      ) : <Autocomplete
        fullWidth
        size="small"
        options={destinations ?? []}
        getOptionLabel={baseLabel}
        isOptionEqualToValue={(option, val) => option.id === val.id}
        value={selectedDestination}
        onChange={(_event, newValue) => onChange(newValue ? newValue.id : null)}
        renderInput={(params) => (
          <TextField
            {...params}
            label={reserveDestinationType === 'customer' ? 'Endereço do cliente' : 'Base'}
            placeholder={
              reserveDestinationType === 'customer'
                ? 'Digite para buscar o endereço do cliente...'
                : 'Digite o nome da base (ex.: Moreia, São Luis)...'
            }
          />
        )}
        noOptionsText="Nenhum endereço encontrado"
        clearText="Limpar (manter base atual)"
        loading={isLoading}
      />}
    </Card>
  )
}

export default BaseDestinationSelector
